import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { addTimer, setTimer as storeSetTimer } from '../../store/time/time';
import useGameInfo from './use-game-info';
import useTime from './use-time';

function useTimer() {
  const dispatch = useDispatch();
  const { isActive, passedTime, startTime } = useTime();
  const { championship } = useGameInfo();
  const isReverseTimer = championship?.settings?.find((s) => s.name === 'countdown_timer')?.value === 'true';
  const periodMinutesStr = championship?.settings?.find((s) => s.name === 'period_time')?.value;
  const periodMinutes = Number(periodMinutesStr ?? 0);
  const [timer, setTimer] = React.useState(passedTime);
  const interval = useRef<NodeJS.Timeout | undefined>(undefined);
  const timeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const prevIsActiveRef = useRef(isActive);
  const initializedRef = useRef(false);

  function changeTimer(milliseconds: number) {
    dispatch(storeSetTimer({ milliseconds }));
  }

  function addToTimer(milliseconds: number) {
    dispatch(addTimer(milliseconds));
  }

  // Initialize timer based on championship settings when countdown is enabled
  useEffect(() => {
    if (initializedRef.current) return;
    if (!isReverseTimer) return;
    if (isActive) return; // don't override running timer
    if (passedTime > 0) return; // already has a value from server/user

    const ms = periodMinutes > 0 ? periodMinutes * 60 * 1000 : 0;
    if (ms > 0) {
      initializedRef.current = true;
      dispatch(storeSetTimer({ milliseconds: ms }));
      setTimer(ms);
    }
  }, [isReverseTimer, periodMinutes, isActive, passedTime, dispatch]);

  useEffect(() => {
    setTimer(passedTime);
  }, [passedTime, startTime]);

  useEffect(() => {
    function updateTimer() {
      setTimer((prevState) => {
        if (isReverseTimer) {
          const next = prevState - 1000;
          return next > 0 ? next : 0;
        }
        return prevState + 1000;
      });
    }

    function startTimer() {
      const left = passedTime % 1000;
      timeout.current = setTimeout(() => {
        updateTimer();
        interval.current = setInterval(updateTimer, 1000);
      }, 1000 - left);
    }

    function stopTimer() {
      clearInterval(interval.current);
      clearTimeout(timeout.current);
    }

    if (isActive) {
      startTimer();
    } else {
      stopTimer();
    }

    return () => {
      stopTimer();
    };
  }, [isActive, passedTime, isReverseTimer]);

  useEffect(() => {
    if (prevIsActiveRef.current && !isActive && isReverseTimer) {
      // On pause, ensure store reflects the countdown value we were showing
      dispatch(storeSetTimer({ milliseconds: timer }));
    }
    prevIsActiveRef.current = isActive;
  }, [isActive, isReverseTimer, timer, dispatch]);

  return { timer, setTimer: changeTimer, addToTimer, isActive };
}

export default useTimer;
